import "../../../../../../assets/transitions/fade.css";
import "./style.css";

import React, { CSSProperties, useEffect } from "react";
import { CSSTransition } from "react-transition-group";
import { useObservable } from "react-use-observable";
import { take } from "rxjs/operators";

import { useControlStorage } from "../../../../../../hooks/controlStorage";
import { useCustomPlayerContext } from "../../../../../../contexts/CustomPlayerContext";
import { usePlayPauseShortcut } from "../../../../../../hooks/playPauseShortcut";
import { useMuteShortcut } from "../../../../../../hooks/muteShortcut";
import { useFullscreenShortcut } from "../../../../../../hooks/fullscreenShortcut";
import { useVolumeShortcut } from "../../../../../../hooks/volumeShortcut";
import { useKeyPressEvent } from "../../../../../../hooks/keypressEvent";
import { useFastShortcut } from "../../../../../../hooks/fastShortcut";
import { useVideoClick } from "../../../../../../hooks/videoClick";
import { activeService } from "../../../../../../services/active";
import { Events, eventsService } from "../../../../../../services/events";
import { remoteControlService } from "../../../../../../services/remoteControl";
import { useSubtitles } from "../../../../../../hooks/subtitles";
import Backdrop from "./Backdrop";
import BigPlay from "./BigPlay";
import Chromecast from "./Chromecast";
import Duration from "./Duration";
import Fullscreen from "./Fullscreen";
import Indicator from "./Indicator";
import Menu from "./Menu";
import PiP from "./PiP";
import Play from "./Play";
import SafeVideoLogo from "./SafeLogo";
import SeekSlider from "./SeekSlider";
import Settings from "./Settings";
import VolumeControl from "./Volume";
import { useIsMobile } from "../../../../../../hooks/useIsMobile";
import CustomLogo from "./CustomLogo";

const Controls: React.FC = () => {
  const subtitles = useSubtitles();
  const isMobile = useIsMobile();
  const transitionNodeRef = React.useRef<HTMLElement>(null);
  const [active] = useObservable(() => activeService.listenIsActive(), []);
  const [remoteControlActive] = useObservable(
    () => remoteControlService.getRemoteControlMode(),
    []
  );
  const { customPlayerPreferences } = useCustomPlayerContext();
  const videoHasSubtitles = Boolean(subtitles && subtitles.length > 0);
  const willShowSubtitlesIcon = Boolean(videoHasSubtitles && customPlayerPreferences.allowSubtitles);
  const willShowSettingsIcon =
    customPlayerPreferences.allowChangeQuality ||
    customPlayerPreferences.allowPlaybackSpeed ||
    willShowSubtitlesIcon;
  const customPlayerPreferencesTextColorStyle: CSSProperties | undefined = customPlayerPreferences.customTextColor
    ? { color: customPlayerPreferences.customTextColor }
    : undefined

  const useShortcuts = () => {
    usePlayPauseShortcut(customPlayerPreferences.allowPlayPause);
    useMuteShortcut(customPlayerPreferences.allowMuteUnmute);
    useFullscreenShortcut(customPlayerPreferences.allowFullscreen);
    useVolumeShortcut({
      isVolumeOptionEnabledForCustomPlayer: customPlayerPreferences.allowVolume,
      areMuteUnmuteOptionsEnabledForCustomPlayer: customPlayerPreferences.allowMuteUnmute,
    });
    useKeyPressEvent();
    useFastShortcut({
      isSkipForwardEnabled: customPlayerPreferences.allowFastAdvance,
      isSkipBackwardsEnabled: customPlayerPreferences.allowFastBack
    });
  };

  useControlStorage();
  useShortcuts();
  useVideoClick({
    transitionNodeRef,
    isPlayPauseEnabled: customPlayerPreferences.allowPlayPause
  });

  useEffect(() => {
    eventsService
      .on(Events.PLAYER_LOADEND)
      .pipe(take(1))
      .subscribe(() => {
        if (transitionNodeRef.current) {
          transitionNodeRef.current.classList.remove("invisible");
        }
      });
  }, []);

  return (
    <CSSTransition
      nodeRef={transitionNodeRef}
      in={!!active}
      timeout={500}
      classNames="fade-transition"
    >
      <section ref={transitionNodeRef} className="controls invisible">
        <header>
          <Backdrop position="top" />
          <div className="controlsContainer">
            <Chromecast />
            {!remoteControlActive && isMobile && willShowSettingsIcon && <Settings />}
          </div>
        </header>

        {customPlayerPreferences.allowPlayPause && <BigPlay />}
        <Indicator />

        <footer>
          <Backdrop position="bottom" />
          <div className="controlsContainer">
            {customPlayerPreferences.allowProgressBar && <SeekSlider />}
            <section>
              <div className="mediaControls">
                {customPlayerPreferences.allowPlayPause && <Play customStyles={customPlayerPreferencesTextColorStyle} />}
                <VolumeControl />
                {customPlayerPreferences.allowProgressBar && <Duration customStyles={customPlayerPreferencesTextColorStyle} />}
              </div>
              <div>
                {!remoteControlActive && customPlayerPreferences.allowPiP && <PiP customStyles={customPlayerPreferencesTextColorStyle} />}
                {!remoteControlActive && !isMobile && willShowSettingsIcon && <Settings customStyles={customPlayerPreferencesTextColorStyle} />}
                {customPlayerPreferences.showLogotype && !customPlayerPreferences.customLogotypeUrl && <SafeVideoLogo />}
                {customPlayerPreferences.showLogotype && customPlayerPreferences.customLogotypeUrl && <CustomLogo customLogotypeUrl={customPlayerPreferences.customLogotypeUrl} />}
                {customPlayerPreferences.allowFullscreen && <Fullscreen customStyles={customPlayerPreferencesTextColorStyle} />}
              </div>
            </section>
          </div>
        </footer>

        <Menu />
      </section>
    </CSSTransition>
  );
};

export default Controls;
